import { ref, shallowRef } from 'vue'

/**
 * Single runtime owner of one image's designer-side state.
 *
 * Mirrors the firmware's `hasp_image_resource_t` (see
 * docs/FIRMWARE-IMAGE-RESOURCE-MANAGER-ANALYSIS.md): the resource owns its
 * decoded bitmap as one property among several, not as its identity. The
 * shared `HTMLImageElement` (`.image`) is exactly analogous to firmware's
 * shared `lv_img_dsc_t` — one decode, referenced by every consumer, owned
 * by the resource rather than by any individual widget. `.thumbnail` is a
 * placeholder for a future smaller preview representation (e.g. for list
 * views), following the same "one more representation the resource owns"
 * shape — not populated by anything yet.
 *
 * Instances are `markRaw()`'d before entering Pinia state (see
 * useImageStore in imageStore.js). Reactivity for the runtime fields comes
 * from their own internal `ref()`/`shallowRef()`, not from Vue deep-proxying
 * the class instance itself — proxying an object that holds a live
 * `HTMLImageElement` is a well-known Vue footgun (ref-unwrapping through a
 * reactive proxy can silently break internal `this._image.value` access).
 * Consumers read the public getters (`resource.image`, `resource.loading`,
 * ...), which stay correctly reactive regardless of whether the containing
 * instance itself is proxied.
 */
export class ImageResource {
  constructor(row) {
    this._syncFromRow(row)

    // --- runtime state — the resource is the only owner of any of this ---
    this._image = shallowRef(null) // shared HTMLImageElement, decoded once, lazily
    this._thumbnail = shallowRef(null) // future preview representation — not populated yet
    this._loading = ref(false)
    this._error = ref(null)
  }

  get image() {
    return this._image.value
  }
  get thumbnail() {
    return this._thumbnail.value
  }
  get loading() {
    return this._loading.value
  }
  get error() {
    return this._error.value
  }

  /**
   * Refresh persisted metadata from a fresh DB row. Leaves decode state
   * (image/loading/error) untouched UNLESS `src` itself changed (a
   * crop/resize edit) — in that case the previously-decoded bitmap is
   * stale, so it's reset and immediately re-decoded, which every already-
   * attached widget picks up via its existing watch on `.image` (they only
   * call ensureDecoded() once, at attach time, so nothing else would ever
   * trigger a re-decode of the new bytes otherwise).
   */
  _syncFromRow(row) {
    this.uuid = row.uuid
    this.id = row.id
    this.filename = row.filename
    this.labelId = row.labelId ?? null
    this.labelName = row.labelName ?? null
    this.imageSize = row.imageSize ?? null

    const srcChanged = this._image && row.src !== this.src // this._image doesn't exist yet on first call, from the constructor
    this.src = row.src // base64 data-URL — the source image data this resource owns

    if (srcChanged) {
      this._image.value = null
      this._error.value = null
      this._loading.value = false
      this.ensureDecoded()
    }
  }

  /**
   * Decode the shared HTMLImageElement, if not already decoded or decoding.
   * Idempotent and safe to call from every consumer — the first caller
   * triggers the decode, everyone else is a no-op that will observe the
   * same result via the reactive `.image`/`.error` getters.
   */
  ensureDecoded() {
    if (this._image.value || this._loading.value) return
    this._loading.value = true
    this._error.value = null
    const img = new Image()
    img.crossOrigin = 'Anonymous'
    img.onload = () => {
      this._image.value = img
      this._loading.value = false
    }
    img.onerror = () => {
      this._error.value = `Failed to decode ${this.filename}`
      this._loading.value = false
    }
    img.src = this.src
  }
}
